import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import api from '@/lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="relative mx-auto mb-8" style={{ width: 'fit-content' }}>
          <img src="/logo_transparent.png" alt="RentalHub" className="object-contain mx-auto block" style={{ width: '90vw', maxWidth: '700px', height: 'auto' }} />
        </div>

        {sent ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-slate-600" />
            </div>
            <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-2">Check your email</h3>
            <p className="text-sm text-slate-500 mb-6">If that email is registered, we've sent a password reset link. Please check your inbox.</p>
            <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 font-medium" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-1">Forgot password?</h3>
            <p className="text-sm text-slate-500 mb-6">Enter your email and we'll send you a reset link</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm" data-testid="forgot-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" required data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Send Reset Link
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 font-medium" data-testid="forgot-back-link">
                <ArrowLeft className="w-3.5 h-3.5 inline mr-1" />Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
